import React, { createContext, useContext, useState } from 'react';
import { useFoodManager, useGoogleDrive, useOpenFoodFacts, useCamera, useAIEstimate } from '../hooks';
import { TABS } from '../utils/constants';

// Create the context
const FoodContext = createContext();

/**
 * Custom hook to use the Food context
 */
export const useFoodContext = () => {
  const context = useContext(FoodContext);
  if (!context) {
    throw new Error('useFoodContext must be used within a FoodProvider');
  }
  return context;
};

/**
 * Food Context Provider component
 */
export const FoodProvider = ({ children }) => {
  // UI State
  const [activeTab, setActiveTab] = useState(TABS.SEARCH);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [productNameInput, setProductNameInput] = useState('');
  const [newFood, setNewFood] = useState({
    name: '',
    gi: '',
    carbs: '',
    portion: 100
  });

  // Initialize food manager hook
  // L'initialisation et la migration silencieuse sont gérées dans useFoodManager.js
  const foodManager = useFoodManager();

  // Initialize Google Drive hook
  const googleDrive = useGoogleDrive(
    foodManager.foods,
    foodManager.selectedFoods,
    foodManager.baseFoods
  );

  // Initialize Open Food Facts hook
  const openFoodFacts = useOpenFoodFacts(
    foodManager.foods,
    foodManager.addFood
  );

  // Initialize Camera hook
  const camera = useCamera((barcode) => {
    if (barcode) {
      setBarcodeInput(barcode);
      openFoodFacts.searchByBarcode(barcode);
      camera.stopCamera();
    }
  });

  // Initialize AI Estimate hook
  const aiEstimate = useAIEstimate();

  /**
   * Handle Google Drive data restoration
   */
  const handleGoogleDriveRestore = async () => {
    try {
      const data = await googleDrive.loadFromGoogleDrive();
      if (data && data.foods) {
        foodManager.loadFoods(data.foods, data.selectedFoods || []);
      }
    } catch (error) {
      console.error('Error restoring from Google Drive:', error);
    }
  };

  /**
   * Add new food manually
   */
  const addNewFood = () => {
    if (newFood.name && newFood.gi && newFood.carbs) {
      const success = foodManager.addFood({
        name: newFood.name,
        gi: parseFloat(newFood.gi),
        carbs: parseFloat(newFood.carbs),
        source: 'manuel'
      });

      if (success) {
        setNewFood({ name: '', gi: '', carbs: '', portion: 100 });
      }
    }
  };

  /**
   * Search by barcode from input
   */
  const searchByBarcodeInput = () => {
    if (barcodeInput) {
      openFoodFacts.searchByBarcode(barcodeInput);
      setBarcodeInput('');
    }
  };

  /**
   * Search by product name from input
   */
  const searchByProductNameInput = () => {
    if (productNameInput) {
      openFoodFacts.searchByName(productNameInput);
      setProductNameInput('');
    }
  };

  /**
   * Clear Open Food Facts search results
   */
  const clearSearchResults = () => {
    openFoodFacts.clearSearchResults();
    setProductNameInput('');
  };

  /**
   * Add AI-estimated food to database
   */
  const addAIEstimatedFood = () => {
    return aiEstimate.addEstimatedFood(foodManager.addFood);
  };

  /**
   * Reset all application state
   */
  const resetApplication = () => {
    foodManager.resetToBaseFoods();
    openFoodFacts.clearAll();
    camera.stopCamera();
    aiEstimate.clearEstimation();
    setBarcodeInput('');
    setProductNameInput('');
    setNewFood({ name: '', gi: '', carbs: '', portion: 100 });
    setActiveTab(TABS.SEARCH);
  };

  /**
   * Get application statistics
   * FIX: Ajout de protection sur availableCameras.length
   */
  const getAppStats = () => {
    return {
      ...foodManager.foodStats,
      googleDrive: {
        connected: googleDrive.isConnected,
        lastSync: googleDrive.lastSyncTime,
        user: googleDrive.googleUser?.getName() || null
      },
      camera: {
        supported: camera.isSupported,
        active: camera.isActive,
        availableCameras: (camera.availableCameras || []).length // Sécurité .length
      }
    };
  };

  // Context value
  const contextValue = {
    activeTab,
    setActiveTab,
    barcodeInput,
    setBarcodeInput,
    productNameInput,
    setProductNameInput,
    newFood,
    setNewFood,
    ...foodManager,
    googleDrive: {
      ...googleDrive,
      restoreData: handleGoogleDriveRestore
    },
    openFoodFacts: {
      ...openFoodFacts,
      searchByBarcodeInput,
      searchByProductNameInput,
      clearResults: clearSearchResults
    },
    camera,
    aiEstimate: {
      ...aiEstimate,
      addEstimatedFood: addAIEstimatedFood
    },
    addNewFood,
    resetApplication,
    getAppStats,
    isLoading: googleDrive.isSyncing || openFoodFacts.isSearching || aiEstimate.isEstimating,
    hasErrors: !!(googleDrive.error || openFoodFacts.error || camera.error || aiEstimate.error),
    errors: {
      googleDrive: googleDrive.error,
      openFoodFacts: openFoodFacts.error,
      camera: camera.error,
      aiEstimate: aiEstimate.error
    }
  };

  return (
    <FoodContext.Provider value={contextValue}>
      {children}
    </FoodContext.Provider>
  );
};
